import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Loader2, PartyPopper, Send } from "lucide-react";
import { Logo } from "@/components/site/Navbar";
import { ThemeToggle } from "@/components/site/ThemeToggle";
import { StepContact } from "@/components/apply/StepContact";
import { StepProfile } from "@/components/apply/StepProfile";
import { StepTier } from "@/components/apply/StepTier";
import { StepId } from "@/components/apply/StepId";
import { StepInterview } from "@/components/apply/StepInterview";
import { StepReview } from "@/components/apply/StepReview";
import { CashNotice } from "@/components/site/CashNotice";
import { LOCAL_TZ } from "@/data/options";
import { api, formatErr } from "@/lib/api";

const STEPS = ["Contact", "Buyer Profile", "Tier", "ID Verification", "Interview", "Review"];
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const OK_TYPES = ["image/jpeg", "image/png", "application/pdf"];

const initial = (tier) => ({
  contact: { full_name: "", email: "", phone_code: "+1", phone: "", country: "", city: "", timezone: LOCAL_TZ },
  profile: { business_name: "", category: "", monthly_volume: "", years_in_business: "", links: "", heard_about: "" },
  tier: ["bronze", "silver", "gold", "platinum"].includes(tier) ? tier : "",
  addons: [],
  files: { id_front: null, id_back: null, selfie: null },
  interview_slots: ["", "", ""],
  agreements: { accurate: false, interview_consent: false, cash_policy: false },
});

function validate(step, f, captcha) {
  const c = f.contact, p = f.profile;
  if (step === 0) {
    if (c.full_name.trim().length < 2) return "Please enter your full name.";
    if (!EMAIL_RE.test(c.email)) return "Please enter a valid email.";
    if (!c.country) return "Please choose your country.";
    if (!c.city.trim()) return "Please enter your city.";
    if (c.phone.trim().length < 4) return "Please enter your phone number.";
  }
  if (step === 1 && (!p.category || !p.monthly_volume || !p.years_in_business)) return "Please complete category, volume and years in business.";
  if (step === 2 && !f.tier) return "Please choose a membership tier.";
  if (step === 3) {
    for (const file of Object.values(f.files)) {
      if (!file) return "Please upload ID front, ID back and a selfie holding your ID.";
      if (!OK_TYPES.includes(file.type)) return "Only JPG, PNG or PDF files are accepted.";
      if (file.size > 10 * 1024 * 1024) return "Each file must be 10MB or smaller.";
    }
  }
  if (step === 4) {
    const s = f.interview_slots;
    if (s.some((x) => !x)) return "Please choose 3 interview times.";
    if (new Set(s).size !== 3) return "Please choose 3 different times.";
  }
  if (step === 5) {
    if (!Object.values(f.agreements).every(Boolean)) return "Please accept all agreements.";
    if (!captcha.answer) return "Please answer the security question.";
  }
  return null;
}

const Confirmation = ({ email }) => (
  <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-10" data-testid="apply-confirmation">
    <div className="mx-auto h-20 w-20 rounded-3xl bg-gradient-to-br from-emerald-500 to-amber-400 grid place-items-center shadow-xl"><PartyPopper className="h-10 w-10 text-white" /></div>
    <h2 className="mt-8 text-3xl sm:text-4xl font-bold">Application received!</h2>
    <p className="mt-4 text-muted-foreground max-w-md mx-auto">Thank you. A confirmation email is on its way to <span className="font-bold text-foreground">{email}</span>. We'll review your ID and reach out to confirm your Zoom interview.</p>
    <CashNotice className="mt-8 max-w-md mx-auto text-left" testId="confirmation-cash-notice" />
    <Link to="/" className="mt-8 inline-flex h-12 items-center px-7 rounded-full bg-primary text-primary-foreground font-bold" data-testid="confirmation-home-button">Back to home</Link>
  </motion.div>
);

export default function Apply() {
  const [params] = useSearchParams();
  const [form, setForm] = useState(() => initial(params.get("tier")));
  const [step, setStep] = useState(0);
  const [dir, setDir] = useState(1);
  const [captcha, setCaptcha] = useState({ id: "", answer: "" });
  const [nonce, setNonce] = useState(0);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const update = (section, key, value) => setForm((f) => ({ ...f, [section]: { ...f[section], [key]: value } }));

  const go = (d) => {
    if (d > 0) {
      const err = validate(step, form, captcha);
      if (err) return toast.error(err);
    }
    setDir(d);
    setStep((s) => s + d);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async () => {
    const err = validate(5, form, captcha);
    if (err) return toast.error(err);
    setBusy(true);
    const fd = new FormData();
    const { files, ...rest } = form;
    fd.append("data", JSON.stringify(rest));
    fd.append("captcha_id", captcha.id);
    fd.append("captcha_answer", captcha.answer);
    Object.entries(files).forEach(([k, v]) => fd.append(k, v));
    try {
      await api.post("/applications", fd);
      setDone(true);
    } catch (e) {
      toast.error(formatErr(e));
      setNonce((n) => n + 1);
    } finally {
      setBusy(false);
    }
  };

  const props = { form, setForm, update };
  const views = [
    <StepContact {...props} />, <StepProfile {...props} />, <StepTier {...props} />,
    <StepId {...props} />, <StepInterview {...props} />,
    <StepReview {...props} captcha={captcha} setCaptcha={setCaptcha} nonce={nonce} />,
  ];

  return (
    <div className="min-h-screen relative overflow-hidden" data-testid="apply-page">
      <div className="absolute -top-40 -right-40 h-[30rem] w-[30rem] rounded-full bg-purple-500/20 blur-3xl pointer-events-none" />
      <div className="absolute top-1/2 -left-40 h-[26rem] w-[26rem] rounded-full bg-emerald-500/20 blur-3xl pointer-events-none" />
      <header className="relative z-10 max-w-4xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between"><Logo /><ThemeToggle /></header>
      <main className="relative z-10 max-w-4xl mx-auto px-5 sm:px-8 pb-20">
        {done ? <Confirmation email={form.contact.email} /> : (
          <>
            <div className="mb-8">
              <div className="flex justify-between text-sm font-bold mb-3">
                <span data-testid="apply-step-label">Step {step + 1} of 6 · {STEPS[step]}</span>
                <span className="text-muted-foreground">{Math.round(((step + 1) / 6) * 100)}%</span>
              </div>
              <div className="h-2.5 rounded-full bg-secondary overflow-hidden" data-testid="apply-progress-bar">
                <motion.div className="h-full vibrant-bar rounded-full" animate={{ width: `${((step + 1) / 6) * 100}%` }} transition={{ type: "spring", stiffness: 120, damping: 20 }} />
              </div>
              <div className="hidden sm:flex justify-between mt-3 text-xs font-semibold text-muted-foreground">
                {STEPS.map((s, i) => <span key={s} className={i <= step ? "text-primary" : ""}>{s}</span>)}
              </div>
            </div>
            <div className="rounded-3xl border border-border bg-card/95 p-6 sm:p-10 shadow-xl overflow-hidden">
              <AnimatePresence mode="wait" custom={dir}>
                <motion.div key={step} initial={{ opacity: 0, x: dir * 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: dir * -40 }} transition={{ duration: 0.25 }}>
                  {views[step]}
                </motion.div>
              </AnimatePresence>
              <div className="mt-10 flex items-center justify-between gap-3">
                <button type="button" onClick={() => go(-1)} disabled={step === 0} className="inline-flex h-12 items-center gap-2 px-5 rounded-full font-semibold hover:bg-secondary transition-colors disabled:opacity-0" data-testid="apply-back-button">
                  <ArrowLeft className="h-4 w-4" /> Back
                </button>
                {step < 5 ? (
                  <button type="button" onClick={() => go(1)} className="group inline-flex h-12 items-center gap-2 px-7 rounded-full bg-primary text-primary-foreground font-bold btn-glow hover:-translate-y-0.5 transition-transform" data-testid="apply-next-button">
                    Continue <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </button>
                ) : (
                  <button type="button" onClick={submit} disabled={busy} className="inline-flex h-12 items-center gap-2 px-7 rounded-full bg-primary text-primary-foreground font-bold btn-glow hover:-translate-y-0.5 transition-transform disabled:opacity-60" data-testid="apply-submit-button">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Submit application
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
